import { useEffect, useState, type ReactNode, type SyntheticEvent } from 'react'
import { Outlet } from 'react-router-dom'
import PageBoundary, { PageLoading } from '../PageBoundary'
import { preloadPath } from '../../lib/lazyPage'
import { useMediaQuery, BREAKPOINTS } from '../../lib/useMediaQuery'
import { cn } from '../../lib/utils'
import { readStored, writeStored } from '@claritio/frontend/lib/storage'

// ENG-133 — the shared shell layout (floating rounded panel + desktop/tablet rail
// + collapse mechanism), extracted from the ENG-130 AppShell so the consultant and
// respondent shells share one layout. Consultant passes a header + mobile drawer +
// a localStorage collapseKey; the respondent shell passes no header, a mobile rail,
// and controlled (route-driven) collapse.

function preloadLink(e: SyntheticEvent) {
  const link = (e.target as Element).closest?.('a[href]')
  if (link) preloadPath(link.getAttribute('href')!)
}

export interface SidebarRenderArgs {
  collapsed: boolean
  canCollapse: boolean
  onToggleCollapse: () => void
  closeMobile: () => void
}

export default function ShellLayout({
  collapseKey,
  collapsed: collapsedProp,
  onToggleCollapse: onToggleProp,
  defaultCollapsed = false,
  mobileMode = 'drawer',
  renderSidebar,
  renderHeader,
  overlays,
  aside,
  mainClassName = 'flex-1 overflow-y-auto px-5 py-6 md:px-7 md:py-7 print:overflow-visible',
  sidebarWidth = 'w-[256px]',
  railWidth = 'w-16',
}: {
  // Uncontrolled mode: persist collapse under this localStorage key (consultant).
  collapseKey?: string
  // Controlled mode: the caller owns collapse (respondent, route-driven).
  collapsed?: boolean
  onToggleCollapse?: () => void
  // Applied only when uncontrolled and the user has no stored preference.
  defaultCollapsed?: boolean
  mobileMode?: 'drawer' | 'rail'
  renderSidebar: (args: SidebarRenderArgs) => ReactNode
  renderHeader?: (args: { openMobile: () => void }) => ReactNode
  overlays?: ReactNode
  // Beside main inside the content panel; it narrows the page rather than covering it.
  aside?: ReactNode
  mainClassName?: string
  // Tailwind width classes for the expanded sidebar and the collapsed rail.
  sidebarWidth?: string
  railWidth?: string
}) {
  const isDesktop = useMediaQuery(BREAKPOINTS.desktop)
  const controlled = collapsedProp !== undefined
  const [internalCollapsed, setInternalCollapsed] = useState(() => {
    if (!collapseKey) return defaultCollapsed
    const stored = readStored(collapseKey)
    return stored === null ? defaultCollapsed : stored === '1'
  })
  const collapsed = controlled ? collapsedProp! : internalCollapsed
  const toggleCollapse = controlled ? (onToggleProp ?? (() => {})) : () => setInternalCollapsed((c) => !c)
  const [mobileOpen, setMobileOpen] = useState(false)
  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileOpen])
  useEffect(() => {
    if (!controlled && collapseKey) writeStored(collapseKey, collapsed ? '1' : '0')
  }, [controlled, collapseKey, collapsed])

  // mobile → drawer (hidden) or rail (forced icon rail); tablet → forced rail;
  // desktop → user preference / route default.
  const railCollapsed = isDesktop ? collapsed : true

  return (
    <div className="shell-ground h-viewport p-0 md:p-2.5 print:h-auto print:bg-none print:p-0">
      <div className="flex h-full gap-2.5 print:h-auto">
        {/* Desktop + tablet rail (+ mobile when mobileMode='rail'): a glass card of its own */}
        <aside
          className={cn(
            'glass shrink-0 overflow-hidden rounded-[20px] print:hidden',
            mobileMode === 'drawer' ? 'hidden md:block' : 'block',
            railCollapsed ? railWidth : sidebarWidth,
          )}
          onMouseOver={preloadLink}
          onFocus={preloadLink}
          onTouchStart={preloadLink}
        >
          {renderSidebar({ collapsed: railCollapsed, canCollapse: isDesktop, onToggleCollapse: toggleCollapse, closeMobile: () => {} })}
        </aside>

        {/* Mobile drawer (drawer mode only) */}
        {mobileMode === 'drawer' && (
          <>
            {mobileOpen && <div className="fixed inset-0 z-40 bg-scrim/30 md:hidden" onClick={() => setMobileOpen(false)} />}
            <aside
              className={cn(
                'fixed inset-y-0 left-0 z-50 p-2 transition-transform md:hidden',
                sidebarWidth,
                mobileOpen ? 'translate-x-0' : '-translate-x-full',
              )}
              onFocus={preloadLink}
              onTouchStart={preloadLink}
            >
              <div className="shell-ground h-full overflow-hidden rounded-[20px]"><div className="glass h-full rounded-[20px]">
                {renderSidebar({ collapsed: false, canCollapse: false, onToggleCollapse: () => {}, closeMobile: () => setMobileOpen(false) })}
              </div></div>
            </aside>
          </>
        )}

        {/* Content column: a calm white panel inside a thin glass frame */}
        <div className="flex min-w-0 flex-1 md:bezel print:p-0 print:shadow-none">
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-content md:rounded-[22px] print:h-auto print:overflow-visible print:rounded-none">
            {renderHeader?.({ openMobile: () => setMobileOpen(true) })}
            <div className="relative flex min-h-0 flex-1 print:block">
              <main className={cn('min-w-0', mainClassName)}>
                <PageBoundary fallback={<PageLoading />}>
                  <Outlet />
                </PageBoundary>
              </main>
              {aside}
            </div>
          </div>
        </div>
      </div>
      {overlays}
    </div>
  )
}
